/**
 * Typed client for the per-user lexruntime OpenClaw settings surface at
 * `/api/lx/openclaw/*`. Every call routes through lexserver's existing
 * per-user-runtime path-based proxy (`/api/lx/*` → user's lexruntime
 * FQDN) — see `per-user-runtime-proxy-plan.md`. Auth is the same Lexiun
 * JWT the chat path uses (Clerk-exchange or seed token).
 *
 * Canonical contract: `ai-docs/lexruntime-openclaw-settings-guide.md`
 * (especially §3 endpoint inventory + §7 error matrix).
 *
 * Handoff §2 hard rule: **never cache org settings**. Every read goes
 * back to `GET /config/get` so org-admin kill-switch flips propagate
 * within one focus cycle. No `meCache`-style module-level cache here.
 */

import { getToken, getCurrentUserId } from "@/lib/lexiun-jwt";
import { newTraceparent } from "@/lib/traceparent";

const ROOT = "/api/lx/openclaw";

// ─── Tier-B-org (read-only from this surface; org edits go through
// lexserver's existing /api/orgs/:orgId/runtime-config rail) ──────────

/** Per-feature kill-switch surface. `true` (or undefined → default true)
 *  = user may edit; `false` = locked by org admin. Granular per-channel
 *  on `channels`. Sourced from the org's `runtimeConfig.openclaw.
 *  userSettingsPolicy` (60s config-sync pull from lexserver). */
export interface UserSettingsPolicy {
  personaSuffix?: boolean;
  locale?: boolean;
  channels?: {
    whatsapp?: boolean;
    telegram?: boolean;
    email?: boolean;
  };
  push?: boolean;
  browser?: boolean;
  voice?: boolean;
  /** Gates the ASSISTANT identity (name/avatar). The canonical lexserver
   *  key; `agentsMain` never existed outside LexIDE (C6, 2026-07-27). */
}

/** Org's curated voice slate, owned by ORG_ADMIN. The user picks
 *  `voice.tts.selectedVoice` from `allowedVoices[]` only. */
export interface OrgVoiceConfig {
  provider?: string;
  allowedVoices?: string[];
  defaultVoice?: string;
  rate?: number;
}

/** Org-side defaults the user can override per-user. */
export interface OrgAgentsConfig {
  defaultIdentity?: {
    name?: string;
    avatarUrl?: string;
  };
}

/** Tier-B-org block reaching the runtime via 60s config-sync pull. */
export interface OrgOpenclawConfig {
  userSettingsPolicy?: UserSettingsPolicy;
  agents?: OrgAgentsConfig;
  voice?: OrgVoiceConfig;
  /** Future Tier-B-org browser policy (allowedDomains, maxIdleMinutes,
   *  maxProfilesPerUser) ships with P4.D — kept open-shape today. */
  browser?: Record<string, unknown>;
  memory?: Record<string, unknown>;
  tools?: Record<string, unknown>;
}

// ─── Tier-B-user (the surface this client mutates) ────────────────────

/** Channel pairing state (per-channel). The connect-status / pairing
 *  flow is owned by the Channels tab; this is the persisted view.
 *  `status` covers both the user-state enum (`error` after a failed
 *  pairing) AND the transient `/connect/status` response (`failed`
 *  during pairing); the UI treats them equivalently. */
export type ChannelStatus =
  | "disconnected"
  | "pending"
  | "connected"
  | "error"
  | "failed";

export interface ChannelState {
  enabled?: boolean;
  status?: ChannelStatus;
  connectedAt?: string;
  lastSync?: string;
  lastError?: string;
  deviceLabel?: string;
}

/** Push subscription metadata kept locally on the runtime; the actual
 *  endpoint + keys live encrypted in events.db (P5.A.1). */
export interface PushSubscription {
  id: string;
  endpoint?: string;
  deviceLabel?: string;
  browser?: string;
  createdAt?: string;
}

/** Per-profile browser metadata (P4.A). `userDataDir` is server-pinned
 *  under `${LEX_STATE_DIR}/lexiun-openclaw/browser-profiles/<id>/`. */
export interface BrowserProfile {
  id: string;
  label: string;
  userDataDir: string;
  createdAt?: string;
  lastUsedAt?: string;
  notes?: string;
}

/** Tier-B-user state shape. All fields optional — a fresh container has
 *  no `settings.json` and `data.user` is `null` from `/config/get`. */
export interface UserState {
  schemaVersion?: number;
  personaSuffix?: string;
  locale?: string;
  channels?: {
    whatsapp?: ChannelState;
    telegram?: ChannelState;
    email?: ChannelState;
  };
  push?: {
    enabled?: boolean;
    subscriptions?: PushSubscription[];
  };
  browser?: {
    enabled?: boolean;
    activeProfileId?: string | null;
    profiles?: BrowserProfile[];
  };
  voice?: {
    tts?: {
      enabled?: boolean;
      selectedVoice?: string;
    };
  };
  agents?: {
    main?: {
      name?: string;
      avatarUrl?: string;
    };
  };
}

/** Merged effective view from `GET /api/lx/openclaw/config/get`. `user`
 *  is `null` when no `settings.json` exists yet (fresh container). */
export interface OpenclawConfig {
  org: OrgOpenclawConfig;
  user: UserState | null;
}

/** Patch body for `PATCH /api/lx/openclaw/config/user`. Same shape as
 *  `UserState` but every field is independently optional — the runtime
 *  deep-merges per top-level key. */
export type UserStatePatch = Partial<UserState>;

// ─── Errors ───────────────────────────────────────────────────────────

/** Domain-specific 404 codes the runtime intentionally raises (vs a
 *  proxy/route-not-found 404 — see `LxOpenclawConfigError.routeNotFound`).
 *  Keep in lockstep with the per-handler responses in the lexruntime
 *  openclaw-config handlers. Extend as new endpoints land — anything
 *  NOT in this list collapses to "the route doesn't exist on this
 *  user's runtime" (handoff §12-bis.4: version-skew detection). */
const DOMAIN_404_CODES = new Set<string>([
  "PROFILE_NOT_FOUND",
  "CRON_JOB_NOT_FOUND",
]);

/** Typed error for `/api/lx/openclaw/*` calls. Carries the canonical
 *  `error` code from §7 of the guide so callers can branch (kill-switch
 *  vs voice-not-allowed vs validation vs auth) AND the HTTP status so
 *  the UI can distinguish 4xx (don't retry — payload is bad) from 5xx
 *  (transient — retry/backoff worthwhile). `disabledKeys` carries the
 *  list of fields the server rejected with USER_SETTING_DISABLED_BY_ORG.
 *
 *  `routeNotFound` (handoff §12-bis.4): true when the response is a
 *  404 whose body either has NO `error.code` OR carries the generic
 *  `NOT_FOUND` code (i.e. the lexserver proxy / runtime dispatcher
 *  said "I don't know this route"). Distinct from a domain 404 like
 *  `CRON_JOB_NOT_FOUND` / `PROFILE_NOT_FOUND` (entity is missing —
 *  the route exists). The Settings shell uses this flag to surface
 *  the "Your runtime is outdated; ask your administrator to upgrade
 *  your workspace" UX instead of a generic "something went wrong",
 *  and to STOP polling loops that would otherwise hammer an
 *  outdated container with thousands of 404s. */
export class LxOpenclawConfigError extends Error {
  status: number;
  code?: string;
  /** Populated on 409 USER_SETTING_DISABLED_BY_ORG. */
  disabledKeys?: string[];
  /** See class docstring — true when the 404 means "this runtime
   *  doesn't have this route yet" (version skew), false when it's a
   *  domain-specific 404. Always false for non-404 statuses. */
  readonly routeNotFound: boolean;
  constructor(
    message: string,
    status: number,
    code?: string,
    disabledKeys?: string[],
  ) {
    super(message);
    this.name = "LxOpenclawConfigError";
    this.status = status;
    this.code = code;
    this.disabledKeys = disabledKeys;
    this.routeNotFound =
      status === 404 && (!code || code === "NOT_FOUND" || !DOMAIN_404_CODES.has(code));
  }
}

async function authedHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    traceparent: newTraceparent(),
  };
}

interface ErrorEnvelope {
  ok?: false;
  error?:
    | string
    | {
        code?: string;
        message?: string;
      };
  message?: string;
  disabledKeys?: string[];
}

async function throwFromResponse(res: Response, route: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as ErrorEnvelope;
  // The runtime sometimes returns `error` as a bare string (legacy
  // handlers) and sometimes as `{ code, message }` (newer handlers).
  // Normalize so callers can rely on `.code`.
  let code: string | undefined;
  let message: string | undefined;
  if (typeof body.error === "string") {
    code = body.error;
    message = body.message;
  } else if (body.error && typeof body.error === "object") {
    code = body.error.code;
    message = body.error.message ?? body.message;
  } else {
    message = body.message;
  }
  throw new LxOpenclawConfigError(
    message ?? `${route} failed: ${res.status}`,
    res.status,
    code,
    body.disabledKeys,
  );
}

// ─── Read endpoints ───────────────────────────────────────────────────

/** GET /api/lx/openclaw/config/get — merged effective view. NO caching
 *  (handoff §2). Callers are expected to refetch on focus / on
 *  kill-switch changes / on every tab-switch back into Settings. */
export async function getOpenclawConfig(): Promise<OpenclawConfig> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/get`, { method: "GET", headers });
  if (!res.ok) await throwFromResponse(res, "GET /api/lx/openclaw/config/get");
  const body = (await res.json()) as { data: OpenclawConfig };
  return body.data;
}

// ─── Write endpoints ──────────────────────────────────────────────────

/** PATCH /api/lx/openclaw/config/user — deep-merges per top-level key
 *  server-side. Throws `LxOpenclawConfigError(409, "USER_SETTING_
 *  DISABLED_BY_ORG")` carrying `disabledKeys` when the org has flipped
 *  off any of the touched fields. */
export async function patchOpenclawUserConfig(
  patch: UserStatePatch,
): Promise<UserState> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/user`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(patch),
  });
  if (!res.ok)
    await throwFromResponse(res, "PATCH /api/lx/openclaw/config/user");
  // Runtime handler shape (handlers.ts:420 — `send(res, 200, { ok: true,
  // data: updated })`) puts the post-patch `UserState` DIRECTLY under
  // `data`, NOT nested under `data.user`. Callers can rely on this.
  const body = (await res.json()) as { data: UserState };
  return body.data;
}

// ─── Browser profile CRUD (canonical guide §3.1/§3.2) ────────────────

/** Body for `POST /browser/profiles`. The server slugifies `label` into
 *  the profile id and pins `userDataDir` under
 *  `${LEX_STATE_DIR}/lexiun-openclaw/browser-profiles/<id>/`. */
export interface CreateBrowserProfileBody {
  label: string;
  notes?: string;
}

/** Server response payload for browser-list / create / delete — both
 *  endpoints return the full updated `browser` view so the SPA can swap
 *  state in one round-trip. Create additionally returns the new row. */
interface BrowserListEnvelope {
  browser: { profiles: BrowserProfile[] };
}
interface BrowserCreateEnvelope extends BrowserListEnvelope {
  profile: BrowserProfile;
}

/** GET /api/lx/openclaw/browser/profiles — full list. Not policy-gated
 *  (preservation §2: the user can always see what they have). */
export async function listBrowserProfiles(): Promise<BrowserProfile[]> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/browser/profiles`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(res, "GET /api/lx/openclaw/browser/profiles");
  const body = (await res.json()) as { data: BrowserListEnvelope };
  return body.data.browser.profiles;
}

/** POST /api/lx/openclaw/browser/profiles — policy-gated. Throws
 *  `LxOpenclawConfigError(409, "PROFILE_LIMIT_REACHED")` when the fleet
 *  is already at 5; UI gates the create button + surfaces a hint. */
export async function createBrowserProfile(
  body: CreateBrowserProfileBody,
): Promise<{ profile: BrowserProfile; profiles: BrowserProfile[] }> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/browser/profiles`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok)
    await throwFromResponse(res, "POST /api/lx/openclaw/browser/profiles");
  const env = (await res.json()) as { data: BrowserCreateEnvelope };
  return { profile: env.data.profile, profiles: env.data.browser.profiles };
}

// ─── Agent Browser live-view extensions (P4.D shipped) ──────────────

/** GET /browser/status — current pane configuration. `vnc.endpoint`, when
 *  present, is rewritten by `getBrowserStatus` (below) to the lexserver-tunneled
 *  WS path `/ws/runtime/<userId>/api/lx/openclaw/browser/vnc/ws`; the LexIDE
 *  client joins it with the page origin + a fresh `?token=` vnc-ticket. `vnc` is
 *  `null` when the browser is disabled — the SPA branches on null to render an
 *  explicit "live view not yet available" overlay (no silent infinite spinner;
 *  no-fallbacks). See ai-docs/lexruntime-noVNC-monkey-patch.md. */
export interface BrowserStatus {
  enabled: boolean;
  activeProfileId: string | null;
  vnc: { endpoint: string } | null;
  /** Supervisor record for the live Chromium process (P4.D.L). `null`
   *  when no Chromium is running for the active profile — either the
   *  user never launched, OR the container was rolled / Chromium
   *  crashed since the activeProfileId was last assigned. The presence
   *  of `chromium` is the authoritative liveness signal; activeProfileId
   *  is settings.json-persisted and outlives PIDs. Shape is intentionally
   *  loose at the client surface — the layout only checks `!= null`. */
  chromium?: {
    pid?: number;
    cdpPort?: number;
    [key: string]: unknown;
  } | null;
}

export async function getBrowserStatus(): Promise<BrowserStatus> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/browser/status`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(res, "GET /api/lx/openclaw/browser/status");
  const body = (await res.json()) as { data: BrowserStatus };
  const status = body.data;
  // P4.D.W: the runtime reports the plugin-relative noVNC path
  // (`/api/lx/openclaw/browser/vnc/ws`). Rewrite it to the lexserver-tunneled,
  // `userId`-prefixed form `/ws/runtime/<userId>/...`; NoVncPane's `buildVncWsUrl`
  // then anchors it to the lexserver WS origin (`VITE_LEXSERVER_WS_URL` in prod;
  // page origin + Vite proxy in dev) so the upgrade reaches lexserver's
  // runtime-ws-proxy VNC branch (→ runtime :18789 ingress → vnc-upgrade-shim →
  // websockify) and NOT the lexide SPA host. `userId` is the Lexiun JWT `sub`.
  // See ai-docs/lexruntime-noVNC-monkey-patch.md §10.
  if (status.vnc?.endpoint) {
    const userId = await getCurrentUserId();
    return {
      ...status,
      vnc: { endpoint: `/ws/runtime/${userId}${status.vnc.endpoint}` },
    };
  }
  return status;
}

/** POST /browser/profiles/:id/launch — sets the active profile + boots
 *  the headed Chromium against its userDataDir. The runtime stamps
 *  `activeProfileId` + `lastUsedAt`. Response carries the updated
 *  `browser` block. */
export async function launchBrowserProfile(
  id: string,
): Promise<{ profiles: BrowserProfile[]; activeProfileId: string | null }> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(id)}/launch`,
    { method: "POST", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/browser/profiles/:id/launch",
    );
  const env = (await res.json()) as {
    data: {
      browser: { profiles: BrowserProfile[]; activeProfileId: string | null };
    };
  };
  return env.data.browser;
}

/** POST /browser/profiles/:id/stop — idempotent. Stops the Chromium
 *  + clears `activeProfileId` if it was set to this id. */
export async function stopBrowserProfile(id: string): Promise<void> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(id)}/stop`,
    { method: "POST", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/browser/profiles/:id/stop",
    );
}

/** GET /browser/vnc-ticket — mints a short-TTL token (default 60 s)
 *  the noVNC pane appends to its WSS URL as `?token=<value>`. The pane
 *  re-mints every `ttlMs - 10s` so the next reconnect uses a fresh
 *  token (handoff §7.4: "Short TTL is non-negotiable"). */
export interface VncTicket {
  token: string;
  expiresAt: string;
  ttlMs: number;
}

export async function getVncTicket(): Promise<VncTicket> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/browser/vnc-ticket`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(res, "GET /api/lx/openclaw/browser/vnc-ticket");
  const body = (await res.json()) as { data: VncTicket };
  return body.data;
}

/** GET /api/lx/dev-vnc-url — lexserver-side resolver that derives the
 *  caller's per-user noVNC WebSocket URL from its userRuntimeRouting
 *  row (P4.D.url, commit 77d4f501). Returns:
 *    - `{ wsUrl: "ws://127.0.0.1:6093", deployTarget: "docker", reason: null }`
 *      for dev where the host port mapping is live.
 *    - `{ wsUrl: null, deployTarget: "azure", reason: "prod-tunnel-not-implemented" }`
 *      until P4.D.W ships the prod-side WS tunnel.
 *
 *  The pane prefers this URL over the per-runtime `vnc.endpoint` (which
 *  today is the still-unimplemented `/api/lx/openclaw/browser/vnc/ws`
 *  stub path on the runtime gateway). When wsUrl is null the pane keeps
 *  the legacy joined-endpoint path so the prod tunnel just works the
 *  moment P4.D.W lands. */
export interface DevVncUrlResponse {
  wsUrl: string | null;
  deployTarget: string;
  reason: string | null;
}

export async function getDevVncUrl(): Promise<DevVncUrlResponse> {
  const headers = await authedHeaders();
  const res = await fetch("/api/lx/dev-vnc-url", {
    method: "GET",
    headers,
  });
  if (!res.ok) await throwFromResponse(res, "GET /api/lx/dev-vnc-url");
  const body = (await res.json()) as { data: DevVncUrlResponse };
  return body.data;
}

/** POST /browser/profiles/:id/pause — Slice P10. Sets the per-profile
 *  `agentPaused` flag the conditional GATE consults; while paused, the
 *  agent's `browser.*` family denies with `agent-paused-by-user`. */
export async function pauseBrowserProfile(id: string): Promise<void> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(id)}/pause`,
    { method: "POST", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/browser/profiles/:id/pause",
    );
}

/** POST /browser/profiles/:id/resume — clears the pause flag. */
export async function resumeBrowserProfile(id: string): Promise<void> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(id)}/resume`,
    { method: "POST", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/browser/profiles/:id/resume",
    );
}

/** POST /browser/profiles/:id/wipe-cookies — `rm -rf <userDataDir>`
 *  for the profile. Profile metadata row stays (recreated on next
 *  launch with a fresh empty userDataDir). */
export async function wipeBrowserProfileCookies(id: string): Promise<void> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(id)}/wipe-cookies`,
    { method: "POST", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/browser/profiles/:id/wipe-cookies",
    );
}

/** DELETE /api/lx/openclaw/browser/profiles/:id — idempotent (returns
 *  200 even for unknown id). Stops the Chromium if active. Per §8.3 the
 *  `userDataDir` is NOT removed from disk — cookies survive deliberately;
 *  the explicit "Wipe cookies" affordance is a future sub-slice. */
export async function deleteBrowserProfile(
  id: string,
): Promise<BrowserProfile[]> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/browser/profiles/${encodeURIComponent(id)}`,
    { method: "DELETE", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "DELETE /api/lx/openclaw/browser/profiles/:id",
    );
  const env = (await res.json()) as { data: BrowserListEnvelope };
  return env.data.browser.profiles;
}

// ─── Cron jobs (P6 — canonical guide §3) ──────────────────────────────

/** Channel id a cron job can target as its delivery sink. Same union as
 *  `ChannelId` over in openclaw-policy — duplicated here to avoid
 *  cross-importing `lib/` from `api/`. Keep in lockstep. */
export type CronDeliveryChannel = "whatsapp" | "telegram" | "email";

/** Cron task = what the agent does each tick. Discriminated union per
 *  the runtime schema (`cron-store.ts:55`). Slice 4 UI only authors the
 *  `prompt` variant; `skill` invocations are deferred (require a UI to
 *  pick a bundled skill id + author its args object — separate slice). */
export type CronTask =
  | { kind: "prompt"; text: string }
  | { kind: "skill"; skillId: string; args?: Record<string, unknown> };

/** Cron delivery target. Three discriminants:
 *   - `chat`             — record the run, do not push externally.
 *   - `push`             — fan out as a Web Push notification.
 *   - `channel`          — pipe through one of the user's paired
 *                          channels (must be enabled+connected, else
 *                          the run fails 409 CRON_DELIVERY_TARGET_
 *                          UNAVAILABLE). */
export type CronDelivery =
  | { kind: "chat" }
  | { kind: "push" }
  | { kind: "channel"; channelId: CronDeliveryChannel };

/** Runtime CronJob shape per `cron-store.ts:CronJobSchema`. */
export interface CronJob {
  id: string;
  label: string;
  /** Cron expression (`croner`-compatible — 5- or 6-field format
   *  depending on whether seconds are included; runtime parses). */
  schedule: string;
  /** IANA tz string (e.g. `America/Sao_Paulo`). */
  timezone: string;
  /** REQUIRED on the runtime side — the create-body type below
   *  defaults it to `true` when the UI doesn't expose a switch. */
  enabled: boolean;
  promptOrTask: CronTask;
  delivery?: CronDelivery;
  thinkingOverride?: "low" | "medium" | "high";
  notes?: string;
  createdAt: string;
  updatedAt: string;
  /** Provenance — `agent` means a chat turn created this job via the
   *  agent-mediated cron flow (handoff §11 hard rule: that path MUST
   *  round-trip through POST /jobs so the audit row + provenance lands). */
  createdBy: "user" | "agent";
  lastUpdatedBy?: "user" | "agent";
  lastRunAt?: string;
  lastRunStatus?: "ok" | "failed" | "skipped" | "running";
  nextRunAt?: string;
}

/** Body for `POST /api/lx/openclaw/config/cron/jobs`. Server stamps id /
 *  createdAt / updatedAt / createdBy / lastUpdatedBy / last+nextRun*.
 *  `enabled` is required server-side; UI defaults to true. */
export type CreateCronJobBody = Omit<
  CronJob,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "createdBy"
  | "lastUpdatedBy"
  | "lastRunAt"
  | "lastRunStatus"
  | "nextRunAt"
>;

/** Per `cron-store.ts:CronJobPatchSchema` — partial without server-
 *  stamped fields. */
export type CronJobPatch = Partial<
  Pick<
    CronJob,
    | "label"
    | "schedule"
    | "timezone"
    | "enabled"
    | "promptOrTask"
    | "delivery"
    | "thinkingOverride"
    | "notes"
  >
>;

/** Per-user fleet cap from `cron-store.ts:MAX_CRON_JOBS_PER_USER`. The
 *  UI mirrors it for pre-disable / counter UX; the runtime is the
 *  authoritative check (409 CRON_JOB_LIMIT_REACHED). */
export const MAX_CRON_JOBS_PER_USER = 20;

// ─── Web push notifications (P5 — canonical guide §3) ────────────────

/** Body for `POST /config/push/subscribe`. `endpoint` + `keys` come
 *  straight out of the browser's `PushSubscription.toJSON()`. */
export interface PushSubscribeBody {
  endpoint: string;
  keys: {
    auth: string;
    p256dh: string;
  };
  deviceLabel?: string;
  browser?: string;
}

/** Agent-readable event record from `events.db.pushEvents` (P2.B).
 *  Returned by `GET /push-events/recent`. */
export interface PushEvent {
  id: string;
  ts: string;
  type:
    | "subscribed"
    | "unsubscribed"
    | "test_sent"
    | "delivered"
    | "clicked"
    | "dismissed"
    | "failed";
  subscriptionId?: string;
  deviceLabel?: string;
  source?: string;
  payload?: Record<string, unknown>;
}

/** GET /config/push/vapid-public-key — returns the runtime's real VAPID
 *  application-server public key (P5.A.2.b shipped — base64-url string
 *  suitable for `PushManager.subscribe({ applicationServerKey })`). */
export async function getPushVapidPublicKey(): Promise<string> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/push/vapid-public-key`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(
      res,
      "GET /api/lx/openclaw/config/push/vapid-public-key",
    );
  const body = (await res.json()) as { data: { publicKey: string } };
  return body.data.publicKey;
}

/** POST /config/push/subscribe — register a browser subscription with
 *  the user's runtime. De-dups by `endpoint`. Records a `subscribed`
 *  event in `events.db.pushEvents`. */
export async function subscribePush(body: PushSubscribeBody): Promise<void> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/push/subscribe`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok)
    await throwFromResponse(res, "POST /api/lx/openclaw/config/push/subscribe");
}

/** POST /config/push/unsubscribe — idempotent (removing an unknown id
 *  returns 200). Not policy-gated (preservation §2). */
export async function unsubscribePush(id: string): Promise<void> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/push/unsubscribe`, {
    method: "POST",
    headers,
    body: JSON.stringify({ id }),
  });
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/config/push/unsubscribe",
    );
}

/** POST /config/push/test — fire a real test push at the named
 *  subscription. The runtime calls `web-push` (P5.A.2.b shipped). */
export async function sendTestPush(id: string): Promise<void> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/push/test`, {
    method: "POST",
    headers,
    body: JSON.stringify({ id }),
  });
  if (!res.ok)
    await throwFromResponse(res, "POST /api/lx/openclaw/config/push/test");
}

/** GET /push-events/recent — recent rows from `events.db.pushEvents`.
 *  Read-only, NOT policy-gated (canonical guide §3.4). */
export async function listRecentPushEvents(): Promise<PushEvent[]> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/push-events/recent`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(res, "GET /api/lx/openclaw/push-events/recent");
  const body = (await res.json()) as { data: { events: PushEvent[] } };
  return body.data.events;
}

// ─── Channel pairing (P3 — canonical guide §3) ────────────────────────



/** Per channel-pairing-handlers.ts:514 — persisted view of a single
 *  channel. Mirrors `ChannelState` but flattens `channelId` into the
 *  envelope for caller convenience. */
export interface ChannelStatusResponse {
  channelId: ChannelId;
  enabled: boolean;
  status: ChannelStatus;
  deviceLabel?: string;
  connectedAt?: string;
  lastSync?: string;
  lastError?: string;
}

/** ChannelId is the same union as the policy-side helper's. Re-stated
 *  here so api/ doesn't have to import from lib/. */
export type ChannelId = "whatsapp" | "telegram" | "email";



/**
 * ── WhatsApp native pairing (QR) ────────────────────────────────────────────
 *
 * Contract FROZEN + measured against a running gateway by the lexruntime lane
 * (handoff `20260724T033433Z`, 2026-07-24). `connect` and `connect/status`
 * return the SAME envelope, so a caller branches on ONE field.
 *
 * WHY THIS EXISTS AT ALL: the previous path prefilled a chat prompt and hoped
 * the concierge would echo the QR back as a data-URI markdown image. That is
 * unfixable — delivery depended on an LLM reproducing a ~5,900-char base64 blob
 * verbatim, and it demonstrably corrupts it (the runtime lane CRC-verified two
 * attempts; the second walked every structural check and still rendered an
 * unscannable quarter-image). The QR must never round-trip through a model.
 */
export type WaConnectStatus =
  /** QR live; `qrDataUrl` + `expiresAt` present. NOT terminal — keep polling. */
  | "awaiting_scan"
  /** Paired, credentials written. Terminal, success. */
  | "connected"
  /** Auth already exists; refused without `force`. Terminal — offer relink. */
  | "already_linked"
  /** TTL lapsed unscanned. Terminal — offer a fresh QR. */
  | "expired"
  /** Auth stabilizing. NOT terminal — retry shortly. */
  | "unstable"
  /** Never paired, or post-logout. Terminal, idle. */
  | "disconnected"
  /** Anything else; read `message`. Terminal. */
  | "error";

/** The one envelope both `connect` and `connect/status` return. */
export interface WaConnectEnvelope {
  status: WaConnectStatus;
  message: string;
  /** Present iff `status === "awaiting_scan"`. */
  qrDataUrl?: string;
  /** ISO-8601 UTC. Present iff `qrDataUrl` is. */
  expiresAt?: string;
}

/**
 * `awaiting_scan` and `unstable` are the only non-terminal states, so a poller
 * that stops on everything else can never hang: `awaiting_scan` self-terminates
 * at `expiresAt` → `expired`.
 */
export function isTerminalWaStatus(status: WaConnectStatus): boolean {
  return status !== "awaiting_scan" && status !== "unstable";
}

/**
 * POST /config/channels/whatsapp/connect
 *
 * ⚠ `force: true` is DESTRUCTIVE-ADJACENT: the runtime's `web.login.start`
 * calls `stopChannel()` UNCONDITIONALLY before issuing the QR, and only
 * restarts if the login connected. So requesting a QR on a live session kills
 * WhatsApp for the full 3-minute window, and if the lawyer never scans they are
 * left with a dead channel. Always behind an explicit confirm.
 *
 * Repeat calls inside the TTL are safe and honest: the runtime returns the
 * CACHED QR without resetting `startedAt`, so `expiresAt` stays pinned to the
 * original issue time and a refresh button cannot lie about the deadline.
 */
export async function startWhatsAppConnect(
  opts: { force?: boolean } = {},
): Promise<WaConnectEnvelope> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/channels/whatsapp/connect`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ force: opts.force === true }),
  });
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/config/channels/whatsapp/connect",
    );
  const env = (await res.json()) as { data: WaConnectEnvelope };
  return env.data;
}

/** GET /config/channels/whatsapp/connect/status — same envelope as `connect`. */
export async function getWhatsAppConnectStatus(): Promise<WaConnectEnvelope> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/channels/whatsapp/connect/status`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(
      res,
      "GET /api/lx/openclaw/config/channels/whatsapp/connect/status",
    );
  const env = (await res.json()) as { data: WaConnectEnvelope };
  return env.data;
}

/** POST /config/channels/:id/disconnect — idempotent (preservation §2).
 *  Native-Telegram semantics (per handback `channel-wizards-lexruntime-handback-2026-06-30.md`
 *  §2.5): deletes the durable credential file (`bot-token.txt`) + stamps
 *  `status:"disconnected"`. Non-telegram ids still route to the retiring
 *  BYO disconnect (unchanged) until the WhatsApp native path lands.
 *  Response now carries `requiresRoll` — the wizard should surface a
 *  restart step WHEN THE FLAG IS TRUE, and must not assume it.
 *  ⚠ CORRECTED 2026-09-13: this said "always true for Telegram post-native".
 *  That is FALSE since `lexruntime-changes`' Option A slice (`9a9922928`,
 *  activate/deactivate on the RUNNING config) — see the twin note on
 *  `ChannelPairTelegramResponse.requiresRoll` for the measurement. The
 *  handback §3 that both notes cited is retracted at source (`55a98e9ee`). */
export interface DisconnectChannelResponse {
  channelId: ChannelId;
  status: "disconnected";
  requiresRoll?: boolean;
}
export async function disconnectChannel(
  id: ChannelId,
): Promise<DisconnectChannelResponse | void> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/config/channels/${encodeURIComponent(id)}/disconnect`,
    { method: "POST", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/config/channels/:id/disconnect",
    );
  // The BYO endpoint returns no body; the native Telegram path returns
  // `{ data: { channelId, status, requiresRoll } }`. Support both.
  try {
    const env = (await res.json()) as { data?: DisconnectChannelResponse };
    return env?.data;
  } catch {
    return;
  }
}

// ─── Channel Setup Wizard endpoints (native path — shipped 2026-06-30) ──
// Per `channel-wizards-lexruntime-handoff/handback-2026-06-30.md`. All go
// through the same `/api/lx/openclaw/config/channels/:id/...` routing and
// respect `userSettingsPolicy.channels.<id>` (403 USER_SETTING_DISABLED_BY_ORG).

/** Telegram bot identity from `getMe()` — cached at pair-time, refetched
 *  live on `/details` (which is NOT the hot /config/get path, so a live
 *  getMe here is fine — a revoked token surfaces as status:"error" +
 *  `lastError` in `/details`). */
export interface TelegramBotIdentity {
  id: number;
  username: string;
  firstName?: string;
  canJoinGroups?: boolean;
  canReadAllGroupMessages?: boolean;
  supportsInlineQueries?: boolean;
}

/** WhatsApp identity, read through from the paired Baileys session (runtime
 *  `ce8cc1127`). The runtime OMITS `identity` entirely while unpaired, so
 *  consumers branch on presence — never on empty strings.
 *
 *  ⚠ `phone` (not `phoneNumber`): it is absent for LID/anonymous ids, which is
 *  why every read falls back through `displayName`. */
export interface WhatsAppIdentity {
  /** E.164-ish number of the paired line; absent for LID/anonymous ids. */
  phone?: string;
  /** The account's WhatsApp profile name (pushName). */
  displayName?: string;
  /** Raw Baileys JID of the paired account. */
  jid?: string;
  /** Label of the linked device, as WhatsApp shows it. */
  deviceLabel?: string;
}

/** Response of `POST /channels/telegram/pair` — the pair endpoint upserts
 *  (first-pair OR rotate) idempotently. `getMe()` IS the auth check;
 *  token is NOT written to disk unless getMe succeeds. */
export interface ChannelPairTelegramResponse {
  identity: TelegramBotIdentity;
  tokenFingerprint: string;
  connectedAt: string;
  /**
   * Whether the pair needs a container roll to take effect. **GATE ON THIS
   * FLAG — never assume its value.**
   *
   * ⚠ CORRECTED 2026-09-13. This said: *"Per handback §3 — always true for
   * Telegram in this build (the native plugin binds bot-token at
   * gateway_start, not hot-reload). Bake the roll step into the wizard
   * permanently."* Every clause of that is now false, and it was the
   * EXPENSIVE direction of wrong: a permanently-baked roll step cold-boots a
   * lawyer's container for nothing, which the operator called "an awful idea"
   * (2026-09-11).
   *
   * What changed: `lexruntime-changes`' Option A slice (`9a9922928`) patches
   * `channels.telegram` on the RUNNING config, and OpenClaw's declared
   * `reload.configPrefixes` restarts only that channel.
   *
   * Measured by `tester-galvao` on the galvão row, rotation complete
   * 2026-09-13T20:54:47Z: `POST …/telegram/pair` → 200, **`requiresRoll`
   * FALSE**, container `af1f9eba` `restartCount 0` and started 4.5h earlier —
   * same container, never restarted — with the token fingerprint advancing on
   * four surfaces including a real DM delivered.
   *
   * The cited handback §3 is retracted at source (`55a98e9ee`); its text is
   * kept verbatim there as the record, with a measured note appended.
   *
   * ⚠ NOT retracted, so do not over-correct: §3's nuance still stands — a
   * `getMe()`/test proves the token pre-roll because it calls the Bot API
   * directly rather than through the runtime.
   *
   * 🚨 **TRUE now has TWO CAUSES, which it never had while it was hard-coded.**
   * Read `activation.state` when you need to tell them apart — the boolean
   * alone cannot. See `activation`.
   *
   * ⚠ **The same field name means something DIFFERENT on `/details`**, where
   * OMITTED means UNKNOWN and never "no roll". Here it is a definite boolean
   * derived from `state !== "applied"`. Do not write a shared helper that
   * treats the two as one contract (`lexruntime-changes`, 2026-09-13).
   */
  requiresRoll: boolean;
  /**
   * How the runtime's activation of the freshly-paired token actually went.
   *
   * 🚨 **A pair that does not activate still returns 200.** An activation
   * failure deliberately NEVER fails the pair — failing the request would throw
   * away a token the runtime has already verified and written
   * (`channel-native-handlers.ts:625-654`). So `pending` and `refused` arrive as
   * ORDINARY SUCCESS and never reach the wizard's `catch`; they are a third
   * outcome, neither activated-success nor error.
   *
   * | state | what happened | what the lawyer needs told |
   * |---|---|---|
   * | `applied` | live now | nothing — it already works |
   * | `pending` | the patch LANDED, the reload had not re-pinned within the settle budget | it will very likely come up on its own |
   * | `refused` | the gateway REFUSED the write, NOTHING changed | the token is saved and materialises on the next boot |
   *
   * ⇒ "reiniciando para ativar" is TRUE for `refused` and OVERSTATED for
   * `pending`. `detail` carries a human-readable reason when there is one.
   */
  activation?: {
    state: "applied" | "pending" | "refused";
    detail?: string;
  };
}

export interface ChannelPairTelegramRequest {
  botToken: string;
}

export async function pairTelegramChannel(
  body: ChannelPairTelegramRequest,
): Promise<ChannelPairTelegramResponse> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/channels/telegram/pair`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok)
    await throwFromResponse(res, "POST /api/lx/openclaw/config/channels/telegram/pair");
  const env = (await res.json()) as { data: ChannelPairTelegramResponse };
  return env.data;
}

/** Response of `GET /channels/:id/details`. `identity` is
 *  channel-specific: `TelegramBotIdentity` for telegram; a `WhatsAppIdentity`
 *  once WA native pairing lands; email is `undefined` for now (no channel).
 *  Discriminated by `channelId` at the consumer site. */
export interface ChannelDetailsResponseBase {
  channelId: ChannelId;
  status: ChannelStatus;
  connectedAt?: string;
  lastSync?: string | null;
  lastError?: string | null;
}
export interface TelegramChannelDetailsResponse
  extends ChannelDetailsResponseBase {
  channelId: "telegram";
  identity?: TelegramBotIdentity;
  tokenFingerprint?: string;
  requiresRoll?: boolean;
}
export interface WhatsAppChannelDetailsResponse
  extends ChannelDetailsResponseBase {
  channelId: "whatsapp";
  identity?: WhatsAppIdentity;
  sessionAge?: string;
}
export interface EmailChannelDetailsResponse extends ChannelDetailsResponseBase {
  channelId: "email";
}
export type ChannelDetailsResponse =
  | TelegramChannelDetailsResponse
  | WhatsAppChannelDetailsResponse
  | EmailChannelDetailsResponse;

export async function getChannelDetails(
  id: ChannelId,
): Promise<ChannelDetailsResponse> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/channels/${encodeURIComponent(id)}/details`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(res, "GET /api/lx/openclaw/config/channels/:id/details");
  const env = (await res.json()) as { data: ChannelDetailsResponse };
  return env.data;
}

/** Response of `POST /channels/:id/reveal-secret`. `secret` is `null` for
 *  WhatsApp (multi-device sessions are opaque device-bound blobs — see
 *  handback §2.3 `note`); populated for Telegram. Rate-limited server-side
 *  (3 / 5 min per channel → 429 REVEAL_RATE_LIMITED). Audit-logged. */
export interface ChannelRevealSecretResponse {
  channelId: ChannelId;
  secret: string | null;
  note?: string;
  revealedAt: string;
  expiresAt: string;
}

export async function revealChannelSecret(
  id: ChannelId,
  reason?: string,
): Promise<ChannelRevealSecretResponse> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/config/channels/${encodeURIComponent(id)}/reveal-secret`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "POST /api/lx/openclaw/config/channels/:id/reveal-secret",
    );
  const env = (await res.json()) as { data: ChannelRevealSecretResponse };
  return env.data;
}

/** Response of `POST /channels/:id/test`. Telegram: DMs the most-recently-
 *  active peer (per handback §2.4) — the peer store's `peers.json` sorted
 *  by `lastActiveAt`. Errors: `400 NO_TEST_TARGET` (no peer yet — SPA
 *  hints "DM the bot and press START"), `502 SEND_FAILED`, `404 NOT_PAIRED`. */
export interface ChannelTestResponse {
  sentAt: string;
  target: string;
  message: string;
}

export async function testChannel(id: ChannelId): Promise<ChannelTestResponse> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/channels/${encodeURIComponent(id)}/test`, {
    method: "POST",
    headers,
    body: "{}",
  });
  if (!res.ok)
    await throwFromResponse(res, "POST /api/lx/openclaw/config/channels/:id/test");
  const env = (await res.json()) as { data: ChannelTestResponse };
  return env.data;
}

/** Per-run record returned by `GET /jobs/:jobId/runs`. Renderer-friendly
 *  shape — keep open for runtime-added fields. */
export interface CronJobRun {
  id: string;
  jobId: string;
  startedAt: string;
  finishedAt?: string;
  status: "running" | "ok" | "error";
  durationMs?: number;
  output?: string;
  error?: string;
  costUsd?: number;
  model?: string;
}

/** GET /jobs — full list for the current user. */
export async function listCronJobs(): Promise<CronJob[]> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/cron/jobs`, {
    method: "GET",
    headers,
  });
  if (!res.ok)
    await throwFromResponse(res, "GET /api/lx/openclaw/config/cron/jobs");
  const body = (await res.json()) as { data: { jobs: CronJob[] } };
  return body.data.jobs;
}

/** POST /jobs — create. Runtime handler (`cron-handlers.ts:187`)
 *  returns `{ ok: true, data: { job } }` — just the new row, NOT the
 *  full list. Callers that want the refreshed list call `listCronJobs`
 *  again after this resolves. */
export async function createCronJob(
  body: CreateCronJobBody,
): Promise<CronJob> {
  const headers = await authedHeaders();
  const res = await fetch(`${ROOT}/config/cron/jobs`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok)
    await throwFromResponse(res, "POST /api/lx/openclaw/config/cron/jobs");
  const env = (await res.json()) as { data: { job: CronJob } };
  return env.data.job;
}

/** PATCH /jobs/:id — partial update. */
export async function patchCronJob(
  id: string,
  patch: CronJobPatch,
): Promise<CronJob> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/config/cron/jobs/${encodeURIComponent(id)}`,
    { method: "PATCH", headers, body: JSON.stringify(patch) },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "PATCH /api/lx/openclaw/config/cron/jobs/:id",
    );
  const body = (await res.json()) as { data: { job: CronJob } };
  return body.data.job;
}

/** DELETE /jobs/:id — idempotent. */
export async function deleteCronJob(id: string): Promise<void> {
  const headers = await authedHeaders();
  const res = await fetch(
    `${ROOT}/config/cron/jobs/${encodeURIComponent(id)}`,
    { method: "DELETE", headers },
  );
  if (!res.ok)
    await throwFromResponse(
      res,
      "DELETE /api/lx/openclaw/config/cron/jobs/:id",
    );
}
